import {
  lazy,
  Suspense,
  type FormEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accessibility,
  AlertTriangle,
  Baby,
  Bookmark,
  Check,
  ChevronRight,
  Clock3,
  LocateFixed,
  MapPin,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Users,
  WifiOff,
} from "lucide-react";
import {
  clearArrivalSession,
  getDueArrivalSession,
  getPendingArrivalSession,
  scheduleArrivalSession,
  type PendingArrivalSession,
} from "../arrivalSession";
import {
  sortCandidates,
  type CandidateSortMode,
} from "../candidateSorting";
import { getDefaultStartFacility } from "../defaultStarts";
import { evaluateToilets, getCurrentlyActiveHazards } from "../engine";
import { formatDateTime, stateMeta } from "../format";
import {
  normalizePlaceQuery,
  searchPlaces,
  type PlaceSearchProvider,
  type PlaceSearchResult,
} from "../geocoding";
import {
  applyRouteEstimates,
  prefetchRouteEstimates,
  type WalkingRouteEstimates,
} from "../routeEstimates";
import type { RouteDestination, WalkingRoute } from "../routing";
import { SPOT_OPTIONS } from "../spots";
import {
  getFavoriteToiletIds,
  getUserNeeds,
  saveUserNeeds,
  toggleFavoriteToilet,
  DEFAULT_USER_NEEDS,
  resetUserPreferences,
} from "../storage";
import type {
  Coordinates,
  DataMode,
  DataSnapshot,
  EvaluatedToilet,
  HazardEvent,
  PendingReport,
  PublicCapabilities,
  PublicCapabilityName,
  ReportPhotoDraft,
  ReportSubmissionResult,
  SavedRegionPack,
  Scenario,
  SpotId,
  StatusObservation,
  ToiletPhoto,
  UserNeeds,
} from "../types";
import { useModalDialog } from "../useModalDialog";
import {
  CandidateList,
  countNearbyCandidates,
} from "./CandidateList";
import {
  ArrivalCheckIn,
  type ArrivalCheckInReport,
} from "./ArrivalCheckIn";
import { HazardPanel } from "./HazardPanel";
import { MobileCandidateDock } from "./MobileCandidateDock";
import { OfflinePanel } from "./OfflinePanel";
import { ReportDialog } from "./ReportDialog";
import { ToiletDetail } from "./ToiletDetail";

const MapView = lazy(() => import("./MapView"));

const CURRENT_HAZARD_SCENARIOS: Scenario[] = ["outage", "earthquake", "event"];
const spotLabels: Record<SpotId, string> = {
  ariake: "有明・東京ビッグサイト",
  "tokyo-dome": "東京ドーム",
  "shibuya-station": "渋谷駅",
  "ikebukuro-station": "池袋駅",
};
const hazardPriority: Record<HazardEvent["effect"], number> = {
  confirmed_closed: 0,
  likely_unavailable: 1,
  inspection_required: 2,
  reduced_capacity: 3,
  high_demand: 4,
  information_only: 5,
};

type PlaceSearchState =
  | { status: "idle" }
  | { status: "loading"; query: string }
  | {
      status: "ready";
      query: string;
      results: PlaceSearchResult[];
      provider: PlaceSearchProvider;
    }
  | { status: "error"; query: string };

type PendingSearchOrigin = {
  label: string;
  coordinates: Coordinates;
  spotId: SpotId;
};

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
const distanceMeters = (from: Coordinates, to: Coordinates) => {
  const latitudeDelta = toRadians(to.latitude - from.latitude);
  const longitudeDelta = toRadians(to.longitude - from.longitude);
  const fromLatitude = toRadians(from.latitude);
  const toLatitude = toRadians(to.latitude);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.sin(longitudeDelta / 2) ** 2 *
      Math.cos(fromLatitude) *
      Math.cos(toLatitude);
  return (
    6_371_000 *
    2 *
    Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  );
};

const coveredSpotFor = (coordinates: Coordinates) =>
  SPOT_OPTIONS.find(
    (spot) =>
      distanceMeters(spot.center, coordinates) <= spot.radiusMeters + 500,
  );

const defaultNeeds: UserNeeds = { ...DEFAULT_USER_NEEDS };

const defaultCapabilities: PublicCapabilities = {
  schemaVersion: 1,
  communityReporting: true,
  photoUpload: true,
  routing: true,
  reasons: {},
};

const MOBILE_LAYOUT_QUERY = "(max-width: 820px)";
const isRouteBlocked = (item: EvaluatedToilet | undefined) =>
  item?.state === "likely_unavailable" ||
  item?.state === "unavailable_confirmed";

const useMobileLayout = () => {
  const [mobile, setMobile] = useState(
    () => window.matchMedia?.(MOBILE_LAYOUT_QUERY).matches ?? false,
  );

  useEffect(() => {
    const media = window.matchMedia?.(MOBILE_LAYOUT_QUERY);
    if (!media) return;
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return mobile;
};

const useOnlineStatus = () => {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return online;
};

function LocationPermissionDialog({
  permissionDenied,
  fallbackLabel,
  onConfirm,
  onClose,
}: {
  permissionDenied: boolean;
  fallbackLabel: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const titleId = useId();
  const dialogRef = useModalDialog<HTMLElement>(onClose);

  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="location-permission-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="drawer-heading">
          <div>
            <p className="dialog-step">
              <LocateFixed aria-hidden="true" />
              {permissionDenied ? "位置情報の設定" : "現在地の利用"}
            </p>
            <h2 id={titleId}>
              {permissionDenied
                ? "位置情報の許可を確認"
                : "現在地を使用しますか？"}
            </h2>
          </div>
        </div>
        <div className="report-review">
          <p>
            {permissionDenied
              ? `ブラウザまたは端末の設定で、このサイトの位置情報を許可してから再試行してください。許可しない場合は${fallbackLabel}から案内します。`
              : "端末の位置情報を、この案内のために一度だけ確認します。正確な位置情報や移動履歴は保存せず、利用状況の報告にも含めません。"}
          </p>
          <div className="report-review-actions">
            <button
              className="secondary-button"
              type="button"
              onClick={onClose}
              data-dialog-initial-focus
            >
              今は使わない
            </button>
            <button className="primary-button" type="button" onClick={onConfirm}>
              {permissionDenied ? "許可設定後に再試行" : "現在地を使用"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export function UserDashboard({
  snapshot,
  dataMode,
  capabilities = defaultCapabilities,
  lastSyncedAt,
  refreshing = false,
  observations,
  saved,
  pendingReports,
  selectedSpotId,
  spotLoading,
  onSpotChange,
  onObservation,
  onReport,
  onSave,
  onDeleteSaved,
  onRefreshSaved,
  onRefresh,
  onOpenOperations,
  online: appOnline,
  syncState,
  appNotice,
  capabilityMessages,
}: {
  snapshot: DataSnapshot;
  dataMode: DataMode;
  capabilities?: PublicCapabilities;
  lastSyncedAt?: string;
  refreshing?: boolean;
  observations: StatusObservation[];
  saved?: SavedRegionPack;
  pendingReports: number;
  selectedSpotId: SpotId;
  spotLoading: boolean;
  onSpotChange: (spotId: SpotId) => void | Promise<void>;
  onObservation: (observation: StatusObservation) => void;
  onReport: (
    input: Omit<PendingReport, "id" | "deviceId" | "reportedAt">,
    photo?: ReportPhotoDraft,
  ) => Promise<ReportSubmissionResult>;
  onSave: () => Promise<void>;
  onDeleteSaved: () => Promise<void>;
  onRefreshSaved: () => Promise<void>;
  onRefresh?: () => Promise<void>;
  onOpenOperations: () => void;
  online?: boolean;
  syncState?: {
    status: "idle" | "loading" | "success" | "error";
    lastSuccessAt?: string;
    errorMessage?: string;
  };
  appNotice?: string;
  capabilityMessages?: Partial<Record<PublicCapabilityName, string>>;
}) {
  const detectedOnline = useOnlineStatus();
  const online = appOnline ?? detectedOnline;
  const mobileLayout = useMobileLayout();
  const defaultStartFacility = getDefaultStartFacility(snapshot.region.spotId);
  const [at, setAt] = useState(() => new Date());
  const [needs, setNeeds] = useState<UserNeeds>({ ...defaultNeeds });
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [routeEstimates, setRouteEstimates] =
    useState<WalkingRouteEstimates>({});
  const [dueArrival, setDueArrival] = useState<PendingArrivalSession>();
  const [arrivalRevision, setArrivalRevision] = useState(0);
  const [origin, setOrigin] = useState<Coordinates>(
    defaultStartFacility.coordinates,
  );
  const [originMode, setOriginMode] = useState<"default" | "gps" | "search">(
    "default",
  );
  const [locationState, setLocationState] = useState<
    "idle" | "locating" | "ready" | "search" | "denied" | "unavailable"
  >("idle");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [placeSearchState, setPlaceSearchState] = useState<PlaceSearchState>({
    status: "idle",
  });
  const [searchFeedback, setSearchFeedback] = useState<string>();
  const [selectedOriginLabel, setSelectedOriginLabel] = useState<string>();
  const [selectedId, setSelectedId] = useState<string>();
  const [hasExplicitSelection, setHasExplicitSelection] = useState(false);
  const [candidateSortMode, setCandidateSortMode] =
    useState<CandidateSortMode>("recommended");
  const [candidateRevealRequest, setCandidateRevealRequest] = useState<{
    id: string;
    sequence: number;
  }>();
  const [detailId, setDetailId] = useState<string>();
  const [reportId, setReportId] = useState<string>();
  const [localPhotos, setLocalPhotos] = useState<Record<string, ToiletPhoto[]>>({});
  const [routeDestination, setRouteDestination] = useState<RouteDestination>();
  const [routeInterruptionNotice, setRouteInterruptionNotice] = useState<string>();
  const [pendingSpotId, setPendingSpotId] = useState<SpotId>();
  const [showHazard, setShowHazard] = useState(false);
  const [showOffline, setShowOffline] = useState(false);
  const [showLocationConfirmation, setShowLocationConfirmation] =
    useState(false);
  const [conditionsOpen, setConditionsOpen] = useState(false);
  const [activeSearchOptionId, setActiveSearchOptionId] = useState<string>();
  const [candidateDockOpen, setCandidateDockOpen] = useState(false);
  const mapColumnRef = useRef<HTMLDivElement>(null);
  const recommendationRef = useRef<HTMLDivElement>(null);
  const conditionsRef = useRef<HTMLElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const locationTriggerRef = useRef<HTMLButtonElement>(null);
  const routeTriggerRef = useRef<HTMLElement | null>(null);
  const routeDestinationRef = useRef<RouteDestination | undefined>(undefined);
  const placeSearchAbortRef = useRef<AbortController | undefined>(undefined);
  const pendingSearchOriginRef = useRef<PendingSearchOrigin | undefined>(
    undefined,
  );
  const localPhotoUrlsRef = useRef<Set<string>>(new Set());
  const sharedCandidateHandledRef = useRef(false);
  const scheduledRouteRef = useRef<string | undefined>(undefined);

  const interruptRoute = useCallback((reason: string) => {
    const hadActiveRoute = Boolean(routeDestinationRef.current);
    scheduledRouteRef.current = undefined;
    clearArrivalSession();
    setDueArrival(undefined);
    if (!hadActiveRoute) return false;
    routeDestinationRef.current = undefined;
    setRouteDestination(undefined);
    setRouteInterruptionNotice(reason);
    return true;
  }, []);

  useEffect(
    () => () => {
      localPhotoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      localPhotoUrlsRef.current.clear();
    },
    [],
  );

  useEffect(() => {
    let active = true;
    void Promise.all([getUserNeeds(), getFavoriteToiletIds()]).then(
      ([savedNeeds, savedFavorites]) => {
        if (!active) return;
        setNeeds(savedNeeds);
        setFavoriteIds(savedFavorites);
        setPreferencesLoaded(true);
      },
      () => {
        if (active) setPreferencesLoaded(true);
      },
    );
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!preferencesLoaded) return;
    void saveUserNeeds(needs).catch(() => undefined);
  }, [
    needs.baby_changing,
    needs.openNow,
    needs.ostomate,
    needs.wheelchair,
    preferencesLoaded,
  ]);

  const addLocalPhoto = useCallback(
    (
      toiletId: string,
      draft: ReportPhotoDraft,
      status: ToiletPhoto["status"],
      photoId = `local-photo-${crypto.randomUUID()}`,
    ) => {
      const objectUrl = URL.createObjectURL(draft.file);
      localPhotoUrlsRef.current.add(objectUrl);
      const localPhoto: ToiletPhoto = {
        id: photoId,
        toiletId,
        kind: draft.kind,
        thumbnailUrl: objectUrl,
        fullUrl: objectUrl,
        submittedAt: new Date().toISOString(),
        status,
      };
      setLocalPhotos((current) => ({
        ...current,
        [toiletId]: [localPhoto, ...(current[toiletId] ?? [])],
      }));
    },
    [],
  );

  useEffect(() => {
    const refreshClock = window.setInterval(() => setAt(new Date()), 60_000);
    return () => window.clearInterval(refreshClock);
  }, []);

  useEffect(() => {
    let timeout: number | undefined;
    const refreshArrival = () => {
      if (timeout !== undefined) window.clearTimeout(timeout);
      timeout = undefined;
      const now = new Date();
      setDueArrival(getDueArrivalSession(now));
      const pendingArrival = getPendingArrivalSession(now);
      if (!pendingArrival) return;
      const dueDelay = Date.parse(pendingArrival.dueAt) - now.getTime();
      const expiryDelay = Date.parse(pendingArrival.expiresAt) - now.getTime();
      const nextDelay = dueDelay > 0 ? dueDelay : expiryDelay;
      if (nextDelay >= 0) {
        timeout = window.setTimeout(
          refreshArrival,
          Math.min(nextDelay + 50, 2_147_000_000),
        );
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshArrival();
    };
    refreshArrival();
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", refreshArrival);
    return () => {
      if (timeout !== undefined) window.clearTimeout(timeout);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", refreshArrival);
    };
  }, [arrivalRevision]);

  const locate = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setLocationState("unavailable");
      return;
    }
    setLocationState("locating");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (
          !interruptRoute(
            "出発地を現在地に変更したため、案内を終了しました。もう一度目的地を選んでください。",
          )
        ) {
          setRouteInterruptionNotice(undefined);
        }
        setOrigin({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        placeSearchAbortRef.current?.abort();
        setSearchQuery("");
        setShowSearchResults(false);
        setPlaceSearchState({ status: "idle" });
        setSearchFeedback(undefined);
        setSelectedOriginLabel(undefined);
        setOriginMode("gps");
        setHasExplicitSelection(false);
        setSelectedId(undefined);
        setCandidateRevealRequest(undefined);
        setLocationState("ready");
        setConditionsOpen(false);
      },
      (error) => {
        if (
          !interruptRoute(
            "現在地を取得できなかったため、既定の出発地に戻しました。もう一度目的地を選んでください。",
          )
        ) {
          setRouteInterruptionNotice(undefined);
        }
        setOrigin(defaultStartFacility.coordinates);
        setOriginMode("default");
        setSearchQuery("");
        setShowSearchResults(false);
        setPlaceSearchState({ status: "idle" });
        setSearchFeedback(undefined);
        setSelectedOriginLabel(undefined);
        setHasExplicitSelection(false);
        setSelectedId(undefined);
        setCandidateRevealRequest(undefined);
        setLocationState(
          error.code === error.PERMISSION_DENIED ? "denied" : "unavailable",
        );
      },
      { enableHighAccuracy: true, timeout: 8_000, maximumAge: 60_000 },
    );
  }, [defaultStartFacility.coordinates, interruptRoute]);

  useEffect(() => {
    if (locationState !== "denied" && locationState !== "unavailable") return;
    locationTriggerRef.current?.focus();
  }, [locationState]);

  useEffect(
    () => () => {
      placeSearchAbortRef.current?.abort();
    },
    [],
  );

  const activeHazards = useMemo(
    () =>
      getCurrentlyActiveHazards(snapshot, at)
        .filter((hazard) => !hazard.isSimulation)
        .sort(
        (left, right) =>
          hazardPriority[left.effect] - hazardPriority[right.effect],
      ),
    [at, snapshot],
  );
  const transitionSpotId = pendingSpotId ?? selectedSpotId;
  const regionTransitioning =
    spotLoading ||
    selectedSpotId !== snapshot.region.spotId ||
    (pendingSpotId !== undefined && pendingSpotId !== snapshot.region.spotId);
  const routingEnabled = capabilities.routing;
  const transitionSpotLabel = spotLabels[transitionSpotId];
  const hazardScenario: Scenario =
    CURRENT_HAZARD_SCENARIOS.find((candidateScenario) =>
      activeHazards.some((hazard) => hazard.scenarios.includes(candidateScenario)),
    ) ?? "normal";
  const publicObservations = useMemo(
    () => observations.filter((observation) => observation.sourceId !== "simulation"),
    [observations],
  );
  const recommendedItems = useMemo(
    () =>
      evaluateToilets(snapshot, {
        at,
        scenario: "normal",
        activeHazardIds: activeHazards.map((hazard) => hazard.id),
        origin,
        needs,
        observations: publicObservations,
        online,
      }),
    [
      at.getTime(),
      activeHazards,
      needs.baby_changing,
      needs.openNow,
      needs.ostomate,
      needs.wheelchair,
      publicObservations,
      online,
      origin.latitude,
      origin.longitude,
      snapshot,
    ],
  );
  const baseSortedItems = useMemo(
    () => sortCandidates(recommendedItems, candidateSortMode),
    [candidateSortMode, recommendedItems],
  );
  const nearbyItemCount = countNearbyCandidates(recommendedItems);
  // The public routing gateway deliberately serializes upstream requests.
  // Prefetch only the best candidate; other routes are resolved after a tap.
  const routePrefetchItems = baseSortedItems
    .filter((item) => !isRouteBlocked(item))
    .slice(0, 1);
  const routeEstimateKey = routePrefetchItems
    .map(
      (item) =>
        `${item.id}:${item.coordinates.latitude}:${item.coordinates.longitude}`,
    )
    .join("|");
  useEffect(() => {
    setRouteEstimates({});
    if (
      !online ||
      !routingEnabled ||
      regionTransitioning ||
      originMode === "gps" ||
      !routeEstimateKey
    ) {
      return;
    }
    const controller = new AbortController();
    void prefetchRouteEstimates(routePrefetchItems, origin, {
      signal: controller.signal,
    })
      .then((estimates) => {
        if (!controller.signal.aborted) setRouteEstimates(estimates);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [
    online,
    origin.latitude,
    origin.longitude,
    originMode,
    regionTransitioning,
    routeEstimateKey,
    routingEnabled,
  ]);
  const sortedItems = useMemo(
    () => applyRouteEstimates(baseSortedItems, routeEstimates),
    [baseSortedItems, routeEstimates],
  );
  const exactRouteIds = useMemo(
    () => new Set(Object.keys(routeEstimates)),
    [routeEstimates],
  );
  const items = regionTransitioning ? [] : sortedItems;
  const favoriteItems = items.filter((item) => favoriteIds.includes(item.id));
  const selected =
    items.find((item) => item.id === selectedId) ??
    (!hasExplicitSelection ? items[0] : undefined);
  const detailItem = items.find((item) => item.id === detailId);
  const reportItem = reportId
    ? items.find((item) => item.id === reportId)
    : undefined;
  const activeDueArrival =
    dueArrival?.spotId === snapshot.region.spotId ? dueArrival : undefined;
  const arrivalItem = activeDueArrival
    ? items.find((item) => item.id === activeDueArrival.toiletId)
    : undefined;
  const arrivalFallbackItem = activeDueArrival
    ? items.find(
        (item) => item.id !== activeDueArrival.toiletId && !isRouteBlocked(item),
      )
    : undefined;
  const simulatedReporting = dataMode === "demo" || dataMode === "api_demo";
  const reportingEnabled = simulatedReporting || capabilities.communityReporting;
  const reportingAvailable = reportingEnabled && !regionTransitioning;
  const photoUploadEnabled =
    reportingAvailable && (simulatedReporting || capabilities.photoUpload);
  const originReady = true;
  const originBasisLabel =
    originMode === "gps"
      ? "現在地"
      : originMode === "search"
        ? selectedOriginLabel ?? "選択した地点"
        : defaultStartFacility.label;
  const navigationAvailable =
    routingEnabled && locationState !== "locating" && !regionTransitioning;
  const routedItem = routeDestination
    ? items.find((item) => item.id === routeDestination.id)
    : undefined;
  const activeRouteDestination =
    navigationAvailable && routedItem && !isRouteBlocked(routedItem)
      ? {
          id: routedItem.id,
          name: routedItem.name,
          coordinates: routedItem.coordinates,
        }
      : undefined;

  useEffect(() => {
    if (!activeDueArrival || !reportingAvailable) return;
    const dockContent = document.querySelector<HTMLElement>(
      ".mobile-candidate-dock__content",
    );
    const focusedInsideDock = Boolean(
      dockContent &&
        document.activeElement &&
        dockContent.contains(document.activeElement),
    );
    setCandidateDockOpen(false);
    if (!focusedInsideDock) return;

    const frame = window.requestAnimationFrame(() => {
      document
        .querySelector<HTMLButtonElement>(".arrival-check-in__choice")
        ?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    activeDueArrival?.spotId,
    activeDueArrival?.toiletId,
    reportingAvailable,
  ]);

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const searchMatches =
    normalizedSearchQuery.length < 2
      ? []
      : items.filter((toilet) =>
          `${toilet.name} ${toilet.nameEn} ${toilet.address}`
            .toLowerCase()
            .includes(normalizedSearchQuery),
        );
  const localSearchResults = searchMatches.slice(0, 5);
  const searchIsActive = showSearchResults && normalizedSearchQuery.length >= 2;
  const placeResults =
    placeSearchState.status === "ready" &&
    placeSearchState.query === normalizePlaceQuery(searchQuery)
      ? placeSearchState.results
      : [];
  const activeFilterCount = Object.values(needs).filter(Boolean).length;
  const searchOptionIds = [
    ...localSearchResults.map((toilet) => `toilet-${toilet.id}`),
    ...placeResults.map((result) => `place-${result.id}`),
  ];

  const selectItem = useCallback(
    (id: string) => {
      const routedId = routeDestinationRef.current?.id;
      if (routedId && routedId !== id) {
        interruptRoute(
          "別のトイレ候補を選択したため、案内を終了しました。案内する候補を確認してください。",
        );
      } else if (!routedId) {
        setRouteInterruptionNotice(undefined);
      }
      setHasExplicitSelection(true);
      setSelectedId(id);
    },
    [interruptRoute],
  );

  const selectMapItem = useCallback(
    (id: string) => {
      selectItem(id);
      if (mobileLayout) setCandidateDockOpen(true);
      setCandidateRevealRequest((current) => ({
        id,
        sequence: (current?.sequence ?? 0) + 1,
      }));
    },
    [mobileLayout, selectItem],
  );

  const changeCandidateSort = useCallback((mode: CandidateSortMode) => {
    setCandidateSortMode(mode);
    setCandidateRevealRequest(undefined);
  }, []);

  const focusRecommendations = useCallback(() => {
    window.requestAnimationFrame(() => {
      if (mobileLayout) {
        const reducedMotion =
          window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
        mapColumnRef.current?.scrollIntoView({
          behavior: reducedMotion ? "auto" : "smooth",
          block: "start",
          inline: "nearest",
        });
        const mapShell = mapColumnRef.current?.querySelector<HTMLElement>(
          ".map-shell",
        );
        if (mapShell) {
          mapShell.tabIndex = -1;
          mapShell.focus({ preventScroll: true });
        }
        return;
      }
      document.getElementById("recommendation-heading")?.focus();
    });
  }, [mobileLayout]);

  const scrollToConditions = useCallback(
    (focusSearch = false) => {
      if (mobileLayout) setConditionsOpen(true);
      window.requestAnimationFrame(() => {
        const target = mobileLayout
          ? conditionsRef.current
          : document.getElementById("area-switcher-label");
        target?.scrollIntoView({
          behavior:
            window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
              ? "auto"
              : "smooth",
          block: "start",
        });
        if (focusSearch) {
          window.requestAnimationFrame(() => searchInputRef.current?.focus());
        } else if (mobileLayout) {
          conditionsRef.current
            ?.querySelector<HTMLElement>(".mobile-conditions-summary")
            ?.focus({ preventScroll: true });
        } else {
          document
            .getElementById("area-switcher-label")
            ?.focus({ preventScroll: true });
        }
      });
    },
    [mobileLayout],
  );

  const applySearchOrigin = useCallback(
    (label: string, coordinates: Coordinates, toiletId?: string) => {
      if (
        !interruptRoute(
          "出発地を変更したため、案内を終了しました。もう一度目的地を選んでください。",
        )
      ) {
        setRouteInterruptionNotice(undefined);
      }
      setOrigin(coordinates);
      setHasExplicitSelection(Boolean(toiletId));
      setSelectedId(toiletId);
      setCandidateRevealRequest(undefined);
      setSearchQuery(label);
      setShowSearchResults(false);
      setSearchFeedback(undefined);
      setSelectedOriginLabel(label);
      setOriginMode("search");
      setLocationState("search");
      setConditionsOpen(false);
      setCandidateDockOpen(false);
      focusRecommendations();
    },
    [focusRecommendations, interruptRoute],
  );

  const requestSpotChange = useCallback(
    async (spotId: SpotId, routeNotice: string) => {
      if (spotId === snapshot.region.spotId && !regionTransitioning) return true;
      setPendingSpotId(spotId);
      setDetailId(undefined);
      setReportId(undefined);
      setShowHazard(false);
      setCandidateDockOpen(false);
      if (!interruptRoute(routeNotice)) setRouteInterruptionNotice(undefined);
      try {
        await onSpotChange(spotId);
        return true;
      } catch {
        setPendingSpotId((current) => (current === spotId ? undefined : current));
        return false;
      }
    },
    [interruptRoute, onSpotChange, regionTransitioning, snapshot.region.spotId],
  );

  const submitPlaceSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = normalizePlaceQuery(searchQuery);
    if (query.length < 2 || query.length > 120) {
      setShowSearchResults(true);
      setSearchFeedback("2文字以上120文字以内で入力してください。");
      return;
    }
    if (placeSearchState.status === "ready" && placeSearchState.query === query) {
      setShowSearchResults(true);
      return;
    }

    placeSearchAbortRef.current?.abort();
    const controller = new AbortController();
    placeSearchAbortRef.current = controller;
    setShowSearchResults(true);
    setSearchFeedback(undefined);
    setPlaceSearchState({ status: "loading", query });
    try {
      const response = await searchPlaces(query, {
        signal: controller.signal,
        spot: snapshot.region.spotId,
      });
      if (placeSearchAbortRef.current !== controller) return;
      setPlaceSearchState({
        status: "ready",
        query,
        results: response.results,
        provider: response.provider,
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      setPlaceSearchState({ status: "error", query });
      setSearchFeedback(
        error instanceof Error && error.message === "geocode_429"
          ? "検索が混み合っています。少し待ってから、もう一度お試しください。"
          : "駅・施設・住所の検索に接続できません。登録トイレの候補は選べます。",
      );
    }
  };

  const selectPlaceResult = async (result: PlaceSearchResult) => {
    const coveredSpot = coveredSpotFor(result.coordinates);
    if (!coveredSpot) {
      setSearchFeedback(
        "この地点は現在のトイレ提供範囲外です。有明・東京ドーム・渋谷・池袋周辺の地点を選んでください。",
      );
      return;
    }
    if (coveredSpot.id === snapshot.region.spotId) {
      applySearchOrigin(result.name, result.coordinates);
      return;
    }

    pendingSearchOriginRef.current = {
      label: result.name,
      coordinates: result.coordinates,
      spotId: coveredSpot.id,
    };
    setSearchFeedback(`${spotLabels[coveredSpot.id]}の候補へ切り替えています。`);
    const changed = await requestSpotChange(
      coveredSpot.id,
      "検索した出発地のエリアへ切り替えるため、案内を終了しました。",
    );
    if (!changed) {
      pendingSearchOriginRef.current = undefined;
      setSearchFeedback(
        "対象エリアを読み込めませんでした。通信状態を確認して、もう一度お試しください。",
      );
    }
  };

  const chooseSearchOption = (optionId: string) => {
    const toiletId = optionId.startsWith("toilet-")
      ? optionId.slice("toilet-".length)
      : undefined;
    if (toiletId) {
      const toilet = localSearchResults.find((candidate) => candidate.id === toiletId);
      if (toilet) {
        applySearchOrigin(toilet.name, toilet.coordinates, toilet.id);
      }
      return;
    }
    const placeId = optionId.startsWith("place-")
      ? optionId.slice("place-".length)
      : undefined;
    const result = placeResults.find((candidate) => candidate.id === placeId);
    if (result) void selectPlaceResult(result);
  };

  useEffect(() => {
    if (
      !searchIsActive ||
      (activeSearchOptionId && !searchOptionIds.includes(activeSearchOptionId))
    ) {
      setActiveSearchOptionId(undefined);
    }
  }, [
    activeSearchOptionId,
    searchIsActive,
    searchOptionIds.join("|"),
  ]);

  const scrollMapIntoView = useCallback((focusRouteCard = false) => {
    setCandidateDockOpen(false);
    window.requestAnimationFrame(() => {
      const reducedMotion =
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
      mapColumnRef.current?.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "start",
        inline: "nearest",
      });
      if (focusRouteCard) {
        document
          .getElementById("map-route-close")
          ?.focus({ preventScroll: true });
      }
    });
  }, []);

  const selectListItem = useCallback(
    (id: string) => {
      selectItem(id);
    },
    [selectItem],
  );

  const toggleFavorite = useCallback((toiletId: string) => {
    void toggleFavoriteToilet(toiletId)
      .then(setFavoriteIds)
      .catch(() => undefined);
  }, []);

  const shareCandidate = useCallback(
    async (item: EvaluatedToilet): Promise<"shared" | "copied"> => {
      const url = new URL(window.location.href);
      url.hash = "";
      url.search = "";
      url.searchParams.set("spot", snapshot.region.spotId);
      url.searchParams.set("toilet", item.id);
      const shareData = {
        title: `${item.name} | 東京トイレマップ`,
        text: `${item.name}：${stateMeta[item.state].label}（${item.freshnessLabel}）`,
        url: url.toString(),
      };
      if (typeof navigator.share === "function") {
        await navigator.share(shareData);
        return "shared";
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url.toString());
      } else {
        const field = document.createElement("textarea");
        field.value = url.toString();
        field.setAttribute("readonly", "");
        field.style.position = "fixed";
        field.style.opacity = "0";
        document.body.append(field);
        field.select();
        const copied = document.execCommand("copy");
        field.remove();
        if (!copied) throw new Error("clipboard_unavailable");
      }
      return "copied";
    },
    [snapshot.region.spotId],
  );

  const handleRouteResolved = useCallback(
    (destinationId: string, route: WalkingRoute) => {
      const destination = items.find((item) => item.id === destinationId);
      if (!destination) return;
      const routeKey = `${snapshot.region.spotId}:${destinationId}:${route.durationSeconds}`;
      if (scheduledRouteRef.current === routeKey) return;
      scheduledRouteRef.current = routeKey;
      setRouteEstimates((current) => ({
        ...current,
        [destinationId]: {
          ...route,
          walkingMinutes: Math.max(1, Math.ceil(route.durationSeconds / 60)),
        },
      }));
      scheduleArrivalSession({
        toiletId: destination.id,
        spotId: snapshot.region.spotId,
        facilityName: destination.name,
        durationSeconds: route.durationSeconds,
      });
      setArrivalRevision((current) => current + 1);
    },
    [items, snapshot.region.spotId],
  );

  const navigate = useCallback(
    (item: EvaluatedToilet) => {
      if (!originReady) {
        scrollToConditions(true);
        return;
      }
      const currentItem = items.find((candidate) => candidate.id === item.id);
      if (
        regionTransitioning ||
        !routingEnabled ||
        !currentItem ||
        isRouteBlocked(currentItem)
      ) {
        return;
      }
      routeTriggerRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      setHasExplicitSelection(true);
      setSelectedId(currentItem.id);
      setRouteInterruptionNotice(undefined);
      const destination = {
        id: currentItem.id,
        name: currentItem.name,
        coordinates: currentItem.coordinates,
      };
      routeDestinationRef.current = destination;
      setRouteDestination(destination);
      setDetailId(undefined);
      scrollMapIntoView(true);
    },
    [
      items,
      originReady,
      regionTransitioning,
      routingEnabled,
      scrollMapIntoView,
      scrollToConditions,
    ],
  );

  const clearRoute = useCallback(() => {
    setRouteInterruptionNotice(undefined);
    routeDestinationRef.current = undefined;
    scheduledRouteRef.current = undefined;
    clearArrivalSession();
    setDueArrival(undefined);
    setRouteDestination(undefined);
    window.requestAnimationFrame(() => {
      const selectedMarker = document.querySelector<HTMLElement>(
        ".map-marker.is-selected",
      );
      if (selectedMarker?.isConnected) {
        selectedMarker.focus({ preventScroll: true });
      } else if (routeTriggerRef.current?.isConnected) {
        routeTriggerRef.current.focus();
      }
    });
  }, []);

  useEffect(() => {
    if (!routeDestination) return;
    if (
      regionTransitioning ||
      !routingEnabled ||
      !routedItem ||
      isRouteBlocked(routedItem)
    ) {
      setRouteInterruptionNotice(
        regionTransitioning
          ? "エリアを切り替えるため、案内を終了しました。新しいエリアの読み込み後に目的地を選んでください。"
          : !routingEnabled
          ? "徒歩案内機能が停止したため、案内を終了しました。"
          : !routedItem
            ? "目的地が利用不可または候補外になったため、案内を終了しました。"
            : "目的地に利用困難の情報が入ったため、案内を終了しました。",
      );
      routeDestinationRef.current = undefined;
      scheduledRouteRef.current = undefined;
      clearArrivalSession();
      setDueArrival(undefined);
      setRouteDestination(undefined);
    }
  }, [regionTransitioning, routeDestination, routedItem, routingEnabled]);

  useEffect(() => {
    const itemIds = new Set(items.map((item) => item.id));
    if (selectedId && !itemIds.has(selectedId)) {
      setSelectedId(undefined);
      setCandidateRevealRequest(undefined);
    }
    if (detailId && !itemIds.has(detailId)) setDetailId(undefined);
    if (reportId && !itemIds.has(reportId)) setReportId(undefined);
  }, [detailId, items, reportId, selectedId]);

  useEffect(() => {
    if (!reportingAvailable && reportId) setReportId(undefined);
  }, [reportId, reportingAvailable]);

  useEffect(() => {
    if (!spotLoading && pendingSpotId === snapshot.region.spotId) {
      setPendingSpotId(undefined);
    }
  }, [pendingSpotId, snapshot.region.spotId, spotLoading]);

  useEffect(() => {
    placeSearchAbortRef.current?.abort();
    const pendingOrigin = pendingSearchOriginRef.current;
    if (pendingOrigin?.spotId === snapshot.region.spotId) {
      pendingSearchOriginRef.current = undefined;
      setOrigin(pendingOrigin.coordinates);
      setLocationState("search");
      setSearchQuery(pendingOrigin.label);
      setShowSearchResults(false);
      setPlaceSearchState({ status: "idle" });
      setSearchFeedback(undefined);
      setSelectedOriginLabel(pendingOrigin.label);
      setOriginMode("search");
      setConditionsOpen(false);
      setCandidateDockOpen(false);
      setHasExplicitSelection(false);
      setSelectedId(undefined);
      setCandidateRevealRequest(undefined);
      setDetailId(undefined);
      setReportId(undefined);
      focusRecommendations();
      return;
    }

    pendingSearchOriginRef.current = undefined;
    setOrigin(defaultStartFacility.coordinates);
    setOriginMode("default");
    setLocationState("idle");
    setSearchQuery("");
    setShowSearchResults(false);
    setPlaceSearchState({ status: "idle" });
    setSearchFeedback(undefined);
    setSelectedOriginLabel(undefined);
    setCandidateDockOpen(false);
    setHasExplicitSelection(false);
    setSelectedId(undefined);
    setCandidateRevealRequest(undefined);
    setDetailId(undefined);
    setReportId(undefined);
  }, [
    defaultStartFacility.coordinates.latitude,
    defaultStartFacility.coordinates.longitude,
    snapshot.region.spotId,
    focusRecommendations,
  ]);

  useEffect(() => {
    if (sharedCandidateHandledRef.current || regionTransitioning) return;
    const parameters = new URLSearchParams(window.location.search);
    const sharedSpotId = parameters.get("spot");
    const sharedToiletId = parameters.get("toilet")?.trim();
    if (!sharedSpotId || !sharedToiletId || sharedToiletId.length > 160) {
      sharedCandidateHandledRef.current = true;
      return;
    }
    if (sharedSpotId !== snapshot.region.spotId) return;
    const sharedItem = items.find((item) => item.id === sharedToiletId);
    if (!sharedItem) return;
    setHasExplicitSelection(true);
    setSelectedId(sharedItem.id);
    setDetailId(sharedItem.id);
    sharedCandidateHandledRef.current = true;
  }, [items, regionTransitioning, snapshot.region.spotId]);

  const submitReport = async (
    input: Omit<PendingReport, "id" | "deviceId" | "reportedAt">,
    photo?: ReportPhotoDraft,
  ) => {
    if (!reportingAvailable) {
      throw new Error("community_reporting_unavailable");
    }
    if (
      dataMode === "demo" ||
      dataMode === "api_demo" ||
      activeHazards.some((hazard) => hazard.isSimulation)
    ) {
      const simulatedId = crypto.randomUUID();
      const observedAt = at.toISOString();
      onObservation({
        id: `simulation-${simulatedId}`,
        toiletId: input.toiletId,
        type: "community",
        status: input.status,
        queueBand: input.queueBand,
        observedAt,
        validUntil: new Date(at.getTime() + 30 * 60_000).toISOString(),
        sourceId: "simulation",
      });
      if (photo) addLocalPhoto(input.toiletId, photo, "local");
      return {
        queued: false,
        simulated: true,
        photoStatus: photo ? ("local" as const) : undefined,
      };
    }

    const response = await onReport(input, photo);
    const observedAt = response.report.reportedAt;
    onObservation({
      id: `local-${response.report.id}`,
      toiletId: input.toiletId,
      type: "community",
      status: input.status,
      queueBand: input.queueBand,
      observedAt,
      validUntil: new Date(new Date(observedAt).getTime() + 30 * 60_000).toISOString(),
      sourceId: "community-reports",
    });
    if (photo) {
      addLocalPhoto(
        input.toiletId,
        photo,
        response.photoStatus === "uploaded"
          ? "pending"
          : response.photoStatus === "not_uploaded"
            ? "failed"
            : "local",
        response.photoId,
      );
    }
    return {
      queued: response.queued,
      simulated: false,
      photoStatus: response.photoStatus,
      photoId: response.photoId,
    };
  };

  const submitArrivalCheckIn = async (report: ArrivalCheckInReport) => {
    if (!activeDueArrival) return;
    await submitReport({
      toiletId: activeDueArrival.toiletId,
      status: report.status,
      queueBand: report.status === "queue" ? report.queueBand : undefined,
    });
    clearArrivalSession();
  };

  const dismissArrivalCheckIn = () => {
    clearArrivalSession();
    setDueArrival(undefined);
  };

  const primaryHazard = activeHazards[0];
  const currentInformationTitle = primaryHazard
    ? `${primaryHazard.title}${activeHazards.length > 1 ? ` ほか${activeHazards.length - 1}件` : ""}`
    : "";
  const dataNotice =
    dataMode === "saved"
      ? `保存データを表示中（${saved ? formatDateTime(saved.savedAt) : "保存日時不明"}）。現在の利用可否ではありません。`
      : dataMode === "api_demo"
        ? "実証データ・現地案内も確認してください。"
        : dataMode === "demo"
          ? "実証データ・現在状況ではありません。"
          : "";
  const dataModeLabel =
    dataMode === "api"
      ? "公開データ"
      : dataMode === "saved"
        ? "保存データ"
        : "実証データ";
  const effectiveLastSyncedAt = syncState?.lastSuccessAt ?? lastSyncedAt;
  const syncErrorNotice =
    syncState?.status === "error"
      ? syncState.errorMessage ??
        "最新情報を取得できませんでした。前回取得した情報を表示しています。"
      : undefined;
  const locationActionLabel =
    locationState === "locating"
      ? "現在地を確認中"
      : locationState === "ready"
        ? "現在地を更新"
        : locationState === "idle" || locationState === "search"
          ? "現在地を使う"
          : "現在地を再試行";
  const locationStatusText = regionTransitioning
    ? `${transitionSpotLabel}の候補を読み込んでいます。`
    : locationState === "idle"
      ? `現在地を使わない場合は${defaultStartFacility.label}から案内します。`
      : locationState === "locating"
        ? "端末の位置情報を一度だけ確認しています。移動履歴として保存しません。"
        : locationState === "ready"
          ? "現在地を基準に表示しています。正確な現在地は報告として送信しません。"
          : locationState === "search"
            ? `${selectedOriginLabel ?? "選択した地点"}から案内します。`
            : locationState === "denied"
              ? `位置情報は許可されていません。${originBasisLabel}から案内します。`
              : `位置情報を取得できません。${originBasisLabel}から案内します。`;
  const locationHasError =
    locationState === "denied" || locationState === "unavailable";
  const renderLocationControl = ({
    compact,
    helpId,
  }: {
    compact: boolean;
    helpId: string;
  }) => (
    <>
      <button
        ref={locationTriggerRef}
        type="button"
        className={`locate-button${compact ? " mobile-map-locate-button" : ""}`}
        onClick={() => setShowLocationConfirmation(true)}
        disabled={locationState === "locating" || regionTransitioning}
        aria-label={locationActionLabel}
        aria-describedby={helpId}
      >
        <LocateFixed aria-hidden="true" />
        {compact ? (
          <span aria-hidden="true">
            {locationState === "locating" ? "確認中…" : "現在地を使用"}
          </span>
        ) : (
          locationActionLabel
        )}
      </button>
      <p
        id={helpId}
        className={
          compact
            ? `mobile-location-status${locationHasError ? " is-error" : " sr-only"}`
            : locationHasError
              ? "location-error"
              : "location-guidance"
        }
        role="status"
        aria-live="polite"
      >
        {locationStatusText}
      </p>
    </>
  );

  const hazardSection = primaryHazard ? (
    <section
      className="hazard-banner has-active-hazard"
      aria-labelledby="current-information-title"
    >
      <span
        className={`hazard-icon ${
          hazardScenario === "outage" ? "is-danger" : ""
        }`}
      >
        {hazardScenario === "event" ? (
          <Users aria-hidden="true" />
        ) : (
          <AlertTriangle aria-hidden="true" />
        )}
      </span>
      <span>
        <small>現在発生中の情報</small>
        <h2 id="current-information-title">{currentInformationTitle}</h2>
        <p>{primaryHazard.description}</p>
      </span>
      <button type="button" onClick={() => setShowHazard(true)}>
        詳細を見る
        <ChevronRight aria-hidden="true" />
      </button>
    </section>
  ) : null;

  const mapColumn = (
    <div className="map-column" ref={mapColumnRef}>
      {mobileLayout && (
        <div
          className={`mobile-map-toolbar${locationHasError ? " has-error" : ""}`}
        >
          {renderLocationControl({
            compact: true,
            helpId: "mobile-location-help",
          })}
        </div>
      )}
      {regionTransitioning ? (
        <section
          className="map-shell map-loading"
          aria-label={`${transitionSpotLabel}の地図を読み込み中`}
          aria-busy="true"
        >
          {transitionSpotLabel}の地図と候補を読み込んでいます…
        </section>
      ) : (
        <Suspense
          fallback={
            <section
              className="map-shell map-loading"
              aria-label={`${snapshot.region.name}の地図を読み込み中`}
            >
              地図を読み込んでいます…
            </section>
          }
        >
          <MapView
            items={items}
            hazards={activeHazards}
            origin={origin}
            originLabel={`出発地：${originBasisLabel}${
              originMode === "default" ? "（GPS未使用時）" : ""
            }`}
            originApproximate={originMode === "default"}
            defaultStartFacility={defaultStartFacility}
            selectedId={selected?.id}
            online={online}
            regionName={snapshot.region.name}
            routeDestination={activeRouteDestination}
            prefetchedRoute={
              activeRouteDestination
                ? routeEstimates[activeRouteDestination.id]
                : undefined
            }
            onSelect={selectMapItem}
            onClearRoute={clearRoute}
            onRouteResolved={handleRouteResolved}
          />
        </Suspense>
      )}
      {routeInterruptionNotice && (
        <p className="route-interruption-note" role="status">
          <AlertTriangle aria-hidden="true" />
          {routeInterruptionNotice}
        </p>
      )}
    </div>
  );

  const listColumn = (
    <div className="list-column" ref={recommendationRef}>
      {regionTransitioning ? (
        <section className="recommendations" aria-labelledby="recommendation-heading">
          <div className="recommendation-heading">
            <div className="recommendation-title">
              <h2 id="recommendation-heading" tabIndex={-1}>
                {transitionSpotLabel}のトイレ候補
              </h2>
            </div>
          </div>
          <div className="candidate-list" aria-busy="true">
            <div className="empty-state" role="status" aria-live="polite">
              <b>エリア情報を読み込んでいます</b>
              <span>完了するまで案内・報告は利用できません。</span>
            </div>
          </div>
        </section>
      ) : (
        <CandidateList
          items={items}
          selectedId={selected?.id}
          sortMode={candidateSortMode}
          onSortChange={changeCandidateSort}
          onSelect={selectListItem}
          onDetail={(item) => setDetailId(item.id)}
          onNavigate={navigate}
          navigationEnabled={navigationAvailable}
          originReady={originReady}
          onRequestOrigin={() => scrollToConditions(true)}
          filtersActive={activeFilterCount > 0}
          onClearFilters={() => setNeeds(defaultNeeds)}
          revealRequest={candidateRevealRequest}
          exactRouteIds={exactRouteIds}
          evaluationTime={at}
          nearbyCount={nearbyItemCount}
          embedded={mobileLayout}
          autoScrollOnMobile={mobileLayout}
        />
      )}
    </div>
  );

  return (
    <div className="user-app">
      <a className="skip-link" href="#main-content">
        本文へ移動
      </a>
      {!mobileLayout && (
        <header id="top" className="app-header">
          <a className="brand" href="#top" aria-label="東京トイレマップ トップページ">
            <b>東京トイレマップ</b>
          </a>
          <div className="header-actions">
            <button type="button" onClick={onOpenOperations}>
              <Settings2 aria-hidden="true" />
              運営
            </button>
            <button type="button" onClick={() => setShowOffline(true)}>
              <Bookmark aria-hidden="true" />
              保存・オフライン
            </button>
          </div>
        </header>
      )}

      {!online && (
        <div className="offline-global" role="status">
          <WifiOff aria-hidden="true" />
          {saved
            ? `オフラインです。${formatDateTime(saved.savedAt)}の保存情報を表示します。`
            : "オフラインです。保存情報がないため、端末内の実証用データを表示します。"}
        </div>
      )}

      <main id="main-content" tabIndex={-1}>
      <h1 className="sr-only">東京トイレマップ トイレ候補検索</h1>
      <section className={`data-status-bar is-${dataMode}`} aria-label="データの状態">
        <span className="data-mode-label">{dataModeLabel}</span>
        <span className="data-sync-time">
          <b>
            {effectiveLastSyncedAt
              ? `${formatDateTime(effectiveLastSyncedAt)}取得`
              : "取得時刻を確認中"}
          </b>
        </span>
        {onRefresh && (
          <button
            type="button"
            onClick={() => void onRefresh()}
            disabled={refreshing || !online || regionTransitioning}
            aria-label={refreshing ? "最新情報を取得中" : "最新情報に更新"}
          >
            <RefreshCw aria-hidden="true" className={refreshing ? "is-spinning" : ""} />
            {refreshing ? "更新中" : "更新"}
          </button>
        )}
        {dataNotice && (
          <p role="status">
            <AlertTriangle aria-hidden="true" />
            {dataNotice}
          </p>
        )}
        {syncErrorNotice && (
          <p className="data-refresh-error" role="alert">
            <AlertTriangle aria-hidden="true" />
            {syncErrorNotice}
          </p>
        )}
        {appNotice && (
          <p className="app-notice" role="status" aria-live="polite">
            {appNotice}
          </p>
        )}
        {!reportingEnabled && (
          <small className="capability-status">
            {capabilityMessages?.communityReporting ??
              "現場報告は現在、閲覧のみです"}
          </small>
        )}
      </section>
      {mobileLayout && (
        <div className="user-workspace mobile-map-workspace">
          {mapColumn}
          <MobileCandidateDock
            open={candidateDockOpen}
            candidateCount={nearbyItemCount}
            onToggle={() => setCandidateDockOpen((current) => !current)}
            onOpenOffline={() => setShowOffline(true)}
            pendingReports={pendingReports}
          >
            {listColumn}
          </MobileCandidateDock>
        </div>
      )}
      {mobileLayout && !regionTransitioning && hazardSection}
      <section
        className={`conditions-panel${conditionsOpen ? " is-open" : ""}`}
        aria-label="場所と条件"
        ref={conditionsRef}
      >
        {mobileLayout && (
          <button
            type="button"
            className="mobile-conditions-summary"
            aria-expanded={conditionsOpen}
            aria-controls="conditions-content"
            onClick={() => setConditionsOpen((current) => !current)}
          >
            <Settings2 aria-hidden="true" />
            <span>
              <b>場所・条件</b>
              <small>
                {originBasisLabel}・
                {activeFilterCount > 0
                  ? `設備条件 ${activeFilterCount}件`
                  : "設備条件なし"}
              </small>
            </span>
            <ChevronRight aria-hidden="true" />
          </button>
        )}
        <div
          id="conditions-content"
          className="conditions-content"
          hidden={mobileLayout && !conditionsOpen}
        >
      <section className="area-switcher" aria-labelledby="area-switcher-label">
        <b
          id="area-switcher-label"
          role="heading"
          aria-level={2}
          tabIndex={-1}
        >
          エリアを選ぶ
        </b>
        <div role="group" aria-labelledby="area-switcher-label">
          {SPOT_OPTIONS.map((spot) => (
            <button
              key={spot.id}
              type="button"
              className={transitionSpotId === spot.id ? "is-active" : ""}
              aria-pressed={transitionSpotId === spot.id}
              disabled={regionTransitioning}
              onClick={() => {
                pendingSearchOriginRef.current = undefined;
                void requestSpotChange(
                  spot.id,
                  "エリアを変更するため、案内を終了しました。新しいエリアの読み込み後に目的地を選んでください。",
                );
              }}
            >
              {spotLabels[spot.id]}
            </button>
          ))}
        </div>
        {regionTransitioning && (
          <span role="status" aria-live="polite">
            {transitionSpotLabel}のエリア情報を読み込んでいます
          </span>
        )}
      </section>
      {!mobileLayout && !regionTransitioning && hazardSection}

      <section className="decision-controls" aria-label="候補の絞り込み">
        <div className="control-group filter-control">
          <span>設備で絞り込む</span>
          {(
            [
              ["wheelchair", "車いす", Accessibility],
              ["ostomate", "オストメイト", ShieldCheck],
              ["baby_changing", "おむつ", Baby],
              ["openNow", "営業中", Clock3],
            ] as const
          ).map(([key, label, Icon]) => (
            <button
              type="button"
              key={key}
              className={needs[key] ? "is-active" : ""}
              onClick={() => setNeeds((current) => ({ ...current, [key]: !current[key] }))}
              aria-pressed={needs[key]}
            >
              <Icon aria-hidden="true" />
              {label}
              {needs[key] && <Check className="filter-check" aria-hidden="true" />}
            </button>
          ))}
          <small className="filter-storage-note">
            条件はこの端末だけに保存されます
          </small>
        </div>
      </section>

      <section
        className="search-location"
        aria-label="出発地を駅・施設・住所から設定"
      >
        <div
          className="search-context"
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              setShowSearchResults(false);
            }
          }}
        >
          <label id="facility-search-label" htmlFor="facility-search">
            <b>出発地を変更</b>
            <span id="facility-search-scope">
              案内候補 {items.length}件は入力中に照合。東京の場所は検索時だけ照合
            </span>
          </label>
          <form className="search-box" onSubmit={submitPlaceSearch}>
            <input
              id="facility-search"
              ref={searchInputRef}
              role="combobox"
              aria-label="出発地を駅・施設・住所から設定"
              aria-autocomplete="list"
              value={searchQuery}
              disabled={regionTransitioning}
              onChange={(event) => {
                placeSearchAbortRef.current?.abort();
                setSearchQuery(event.target.value);
                setShowSearchResults(true);
                setPlaceSearchState({ status: "idle" });
                setSearchFeedback(undefined);
                setActiveSearchOptionId(undefined);
              }}
              onFocus={() => setShowSearchResults(true)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setShowSearchResults(false);
                  setActiveSearchOptionId(undefined);
                  return;
                }
                if (
                  (event.key === "ArrowDown" || event.key === "ArrowUp") &&
                  searchOptionIds.length > 0
                ) {
                  event.preventDefault();
                  setShowSearchResults(true);
                  const currentIndex = activeSearchOptionId
                    ? searchOptionIds.indexOf(activeSearchOptionId)
                    : -1;
                  const nextIndex =
                    event.key === "ArrowDown"
                      ? currentIndex < searchOptionIds.length - 1
                        ? currentIndex + 1
                        : 0
                      : currentIndex > 0
                        ? currentIndex - 1
                        : searchOptionIds.length - 1;
                  setActiveSearchOptionId(searchOptionIds[nextIndex]);
                  return;
                }
                if (event.key === "Enter" && activeSearchOptionId) {
                  event.preventDefault();
                  chooseSearchOption(activeSearchOptionId);
                }
              }}
              placeholder="例：東京ビッグサイト、駅名、住所"
              autoComplete="off"
              aria-describedby="facility-search-scope"
              aria-expanded={searchIsActive}
              aria-controls={searchIsActive ? "facility-search-results" : undefined}
              aria-activedescendant={
                searchIsActive && activeSearchOptionId
                  ? `search-option-${activeSearchOptionId}`
                  : undefined
              }
            />
            <button
              type="submit"
              className="place-search-button"
              aria-label={
                placeSearchState.status === "loading"
                  ? "場所を検索中"
                  : "場所を検索"
              }
              disabled={
                normalizePlaceQuery(searchQuery).length < 2 ||
                placeSearchState.status === "loading" ||
                regionTransitioning
              }
            >
              <Search aria-hidden="true" />
            </button>
            {searchIsActive && (
              <div
                className="search-results"
              >
                <p role="status" aria-live="polite">
                  {placeSearchState.status === "loading"
                    ? "東京の駅・施設・住所を検索しています"
                    : placeSearchState.status === "ready"
                      ? `登録トイレ ${searchMatches.length}件、場所 ${placeResults.length}件`
                      : `登録トイレ ${searchMatches.length}件。Enterまたは虫眼鏡ボタンで駅・建物・住所も探せます`}
                </p>
                {searchFeedback && (
                  <div className="search-feedback" role="alert">
                    {searchFeedback}
                  </div>
                )}
                <div
                  id="facility-search-results"
                  className="search-option-list"
                  role="listbox"
                  aria-label="施設・場所の検索結果"
                >
                  {localSearchResults.length > 0 && (
                    <div role="group" aria-label="登録トイレ">
                      <b className="search-group-title" aria-hidden="true">
                        登録トイレ
                      </b>
                    {localSearchResults.map((toilet) => (
                      <button
                        type="button"
                        key={toilet.id}
                        id={`search-option-toilet-${toilet.id}`}
                        role="option"
                        aria-selected={
                          activeSearchOptionId === `toilet-${toilet.id}`
                        }
                        className={
                          activeSearchOptionId === `toilet-${toilet.id}`
                            ? "is-active"
                            : undefined
                        }
                        onMouseEnter={() =>
                          setActiveSearchOptionId(`toilet-${toilet.id}`)
                        }
                        onClick={() =>
                          applySearchOrigin(
                            toilet.name,
                            toilet.coordinates,
                            toilet.id,
                          )
                        }
                      >
                        <MapPin aria-hidden="true" />
                        <span>
                          <b>{toilet.name}</b>
                          <small>{toilet.address}</small>
                        </span>
                      </button>
                    ))}
                    </div>
                  )}
                  {placeSearchState.status === "ready" &&
                    placeResults.length > 0 && (
                    <div role="group" aria-label="駅・建物・住所">
                      <b className="search-group-title" aria-hidden="true">
                        駅・建物・住所
                      </b>
                    {placeResults.map((result) => {
                      const coveredSpot = coveredSpotFor(result.coordinates);
                      return (
                        <button
                          type="button"
                          key={result.id}
                          id={`search-option-place-${result.id}`}
                          role="option"
                          aria-selected={
                            activeSearchOptionId === `place-${result.id}`
                          }
                          className={
                            activeSearchOptionId === `place-${result.id}`
                              ? "is-active"
                              : undefined
                          }
                          onMouseEnter={() =>
                            setActiveSearchOptionId(`place-${result.id}`)
                          }
                          onClick={() => void selectPlaceResult(result)}
                          disabled={regionTransitioning}
                        >
                          <MapPin aria-hidden="true" />
                          <span>
                            <b>{result.name}</b>
                            <small>{result.displayName}</small>
                            <em
                              className={
                                coveredSpot
                                  ? "search-coverage is-supported"
                                  : "search-coverage"
                              }
                            >
                              {coveredSpot
                                ? coveredSpot.id === snapshot.region.spotId
                                  ? "選択中エリアの基準点にできます"
                                  : `${spotLabels[coveredSpot.id]}へ切り替えます`
                                : "現在のトイレ提供範囲外"}
                            </em>
                          </span>
                        </button>
                      );
                    })}
                    </div>
                  )}
                </div>
                {placeSearchState.status === "ready" && (
                  <>
                    {placeResults.length === 0 && (
                      <span className="search-empty">
                        駅・施設・住所が見つかりませんでした。都道府県や区名を加えてお試しください。
                      </span>
                    )}
                    <small className="search-attribution">
                      検索結果：{placeSearchState.provider.name}・
                      <a
                        href={placeSearchState.provider.attributionUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {placeSearchState.provider.attribution}
                      </a>
                    </small>
                  </>
                )}
                {placeSearchState.status !== "ready" &&
                  localSearchResults.length === 0 && (
                    <span className="search-empty">
                      登録トイレには一致しません。虫眼鏡ボタンで駅・建物・住所を探せます。
                    </span>
                  )}
              </div>
            )}
          </form>
        </div>
        {!mobileLayout &&
          renderLocationControl({ compact: false, helpId: "location-help" })}
      </section>
        </div>
      </section>

      {!mobileLayout && (
        <div className="user-workspace">
          {mapColumn}
          {listColumn}
        </div>
      )}
      </main>

      <footer className="app-footer">
        <div className="footer-identity">
          <b>東京トイレマップ</b>
          <span>東京都オープンデータ活用プロトタイプ（東京都公式サイトではありません）</span>
          <p>
            表示は利用可能性の判断支援です。実災害時は自治体・施設・現地の案内を優先してください。
          </p>
        </div>
        <nav aria-label="サイト情報">
          <a href="/site-information.html#policy">サイトポリシー</a>
          <a href="/site-information.html#accessibility">アクセシビリティ方針</a>
          <a href="/site-information.html#privacy">個人情報保護</a>
          <a href="/site-information.html#contact">お問い合わせ</a>
        </nav>
        <small>
          サイト情報更新日：2026年8月4日
          <br />© 2026 984mbo and project contributors
        </small>
      </footer>

      {activeDueArrival && reportingAvailable ? (
        <div className="arrival-check-in-shell">
          <ArrivalCheckIn
            facilityName={activeDueArrival.facilityName}
            onSubmit={submitArrivalCheckIn}
            onDismiss={dismissArrivalCheckIn}
            onOpenFullReport={
              arrivalItem
                ? () => {
                    dismissArrivalCheckIn();
                    setReportId(arrivalItem.id);
                  }
                : undefined
            }
            fallbackName={arrivalFallbackItem?.name}
            onNavigateFallback={
              arrivalFallbackItem
                ? () => {
                    dismissArrivalCheckIn();
                    navigate(arrivalFallbackItem);
                  }
                : undefined
            }
          />
        </div>
      ) : null}

      {showLocationConfirmation ? (
        <LocationPermissionDialog
          permissionDenied={locationState === "denied"}
          fallbackLabel={originBasisLabel}
          onClose={() => setShowLocationConfirmation(false)}
          onConfirm={() => {
            setShowLocationConfirmation(false);
            locate();
          }}
        />
      ) : null}

      {detailItem && (
        <ToiletDetail
          item={detailItem}
          sources={snapshot.sources}
          localPhotos={localPhotos[detailItem.id] ?? []}
          enableRemotePhotos={dataMode === "api"}
          reportEnabled={reportingAvailable}
          reportIsSimulation={simulatedReporting}
          navigationEnabled={navigationAvailable}
          originReady={originReady}
          onRequestOrigin={() => {
            setDetailId(undefined);
            scrollToConditions(true);
          }}
          navigationUnavailableReason={capabilityMessages?.routing}
          reportUnavailableReason={capabilityMessages?.communityReporting}
          favorite={favoriteIds.includes(detailItem.id)}
          hasExactRoute={exactRouteIds.has(detailItem.id)}
          onToggleFavorite={() => toggleFavorite(detailItem.id)}
          onShare={() => shareCandidate(detailItem)}
          onClose={() => setDetailId(undefined)}
          onNavigate={() => navigate(detailItem)}
          onReport={() => {
            if (!reportingAvailable) return;
            setDetailId(undefined);
            setReportId(detailItem.id);
          }}
        />
      )}
      {reportingAvailable && reportId && reportItem && (
        <ReportDialog
          item={reportItem}
          enablePhotoUpload={photoUploadEnabled}
          simulated={simulatedReporting}
          onClose={() => setReportId(undefined)}
          onSubmit={submitReport}
          onViewDetail={() => {
            setReportId(undefined);
            setDetailId(reportItem.id);
          }}
        />
      )}
      {showOffline && (
        <OfflinePanel
          snapshot={snapshot}
          saved={saved}
          online={online}
          pendingReports={pendingReports}
          onSave={onSave}
          onDelete={onDeleteSaved}
          onRefresh={onRefreshSaved}
          onClose={() => setShowOffline(false)}
          needs={needs}
          favoriteItems={favoriteItems}
          onEditNeeds={() => {
            setShowOffline(false);
            scrollToConditions();
          }}
          onSelectFavorite={(item) => {
            setShowOffline(false);
            setHasExplicitSelection(true);
            setSelectedId(item.id);
            setDetailId(item.id);
          }}
          onResetPreferences={async () => {
            await resetUserPreferences();
            setNeeds({ ...defaultNeeds });
            setFavoriteIds([]);
          }}
        />
      )}
      {showHazard && (
        <HazardPanel
          hazards={activeHazards}
          sources={snapshot.sources}
          alternatives={items.filter(
            (item) =>
              !activeHazards.some((hazard) => hazard.affectedToiletIds.includes(item.id)),
          )}
          originReady={originReady}
          onClose={() => setShowHazard(false)}
          onSelectAlternative={(item) => {
            selectItem(item.id);
            setShowHazard(false);
            if (mobileLayout) {
              scrollMapIntoView();
            } else {
              recommendationRef.current?.scrollIntoView({ behavior: "smooth" });
            }
          }}
        />
      )}
    </div>
  );
}
